/**
 * Partner-Link-Modal: paste a URL → preview parsed merchant data → swap reel.
 *
 * Two modes:
 *   - mode="apply": overwrite the existing reel's content (timing/animation
 *                   stay identical, only text/logo/products are swapped).
 *   - mode="clone": create a NEW workspace reel from the source so the
 *                   original stays untouched.
 *
 * The preview step shows exactly which scenes will receive new data and what
 * the parsed payload looks like (logo, products, …) so the user can confirm
 * before persisting.
 */
import React, { useEffect, useState } from "react";
import { api, type PartnerData, type WorkspaceReelLean } from "../api";

type Mode = "apply" | "clone";

type Props = {
  open: boolean;
  onClose: () => void;
  reel: WorkspaceReelLean | null;
  defaultMode?: Mode;
  /** Called after a successful apply/clone. Passes the resulting reel id. */
  onApplied?: (reelId: string, mode: Mode) => void;
};

const SAMPLE_URLS = [
  "https://allyver.com/dekomi",
  "https://allyver.com/jusatech",
];

export const PartnerLinkModal: React.FC<Props> = ({
  open,
  onClose,
  reel,
  defaultMode = "clone",
  onApplied,
}) => {
  const [url, setUrl] = useState<string>("");
  const [mode, setMode] = useState<Mode>(defaultMode);
  const [partner, setPartner] = useState<PartnerData | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [cloneTitle, setCloneTitle] = useState<string>("");

  useEffect(() => {
    if (!open) {
      setUrl("");
      setPartner(null);
      setErr(null);
      setMode(defaultMode);
      setCloneTitle("");
    }
  }, [open, defaultMode]);

  useEffect(() => {
    if (partner && reel && !cloneTitle) {
      setCloneTitle(`${reel.title} · ${partner.name}`);
    }
  }, [partner, reel, cloneTitle]);

  if (!open || !reel) return null;

  const onPreview = async (): Promise<void> => {
    const trimmed = url.trim();
    if (!trimmed) {
      setErr("Bitte URL eingeben.");
      return;
    }
    setLoading(true);
    setErr(null);
    setPartner(null);
    try {
      const data = await api.partnerExtract(trimmed);
      setPartner(data);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const onConfirm = async (): Promise<void> => {
    if (!partner) return;
    setApplying(true);
    setErr(null);
    try {
      const result =
        mode === "clone"
          ? await api.partnerCloneReel(reel.id, {
              partner,
              cloneAs: cloneTitle.trim() || undefined,
            })
          : await api.partnerApplyToReel(reel.id, { partner });
      onApplied?.(result.reel.id, mode);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setApplying(false);
    }
  };

  const itemKindCounts = (partner?.items ?? []).reduce<Record<string, number>>((acc, it) => {
    acc[it.kind] = (acc[it.kind] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal partner-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h2>🔗 Partner-Link einfügen</h2>
            <p className="small muted">
              Übernimmt Name, Logo, Stadt, Branche & Angebote aus der URL — Video
              & Timing bleiben unverändert.
            </p>
            <p className="small muted" style={{ marginTop: 4 }}>
              Quell-Reel: <strong>{reel.title}</strong>{" "}
              <span style={{ opacity: 0.6 }}>
                · {reel.sceneCount} Szenen · {reel.format}
              </span>
            </p>
          </div>
          <button className="btn ghost" onClick={onClose} disabled={applying}>
            ✕
          </button>
        </div>

        <div className="partner-row">
          <label className="partner-label">URL</label>
          <div className="partner-input-row">
            <input
              type="url"
              className="partner-input"
              placeholder="https://allyver.com/dekomi"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void onPreview();
              }}
              disabled={loading || applying}
              autoFocus
            />
            <button
              className="btn primary"
              onClick={() => void onPreview()}
              disabled={loading || applying || !url.trim()}
            >
              {loading ? "…" : "Daten laden"}
            </button>
          </div>
          <div className="partner-samples">
            {SAMPLE_URLS.map((s) => (
              <button
                key={s}
                className="partner-sample"
                onClick={() => setUrl(s)}
                disabled={loading || applying}
                type="button"
              >
                {s.replace(/^https?:\/\//, "")}
              </button>
            ))}
          </div>
        </div>

        {err && <div className="warn-banner">{err}</div>}

        {partner && (
          <div className="partner-preview">
            <div className="partner-preview-head">
              {partner.logoUrl ? (
                <img
                  className="partner-logo"
                  src={partner.logoUrl}
                  alt={partner.name}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : (
                <div className="partner-logo-fallback">
                  {partner.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <h3 className="partner-name">{partner.name}</h3>
                <div className="partner-meta">
                  {partner.type && <span className="partner-chip">{partner.type}</span>}
                  {partner.category && (
                    <span className="partner-chip">{partner.category}</span>
                  )}
                  {partner.location && (
                    <span className="partner-chip">📍 {partner.location}</span>
                  )}
                </div>
                <div className="partner-url small muted">
                  {partner.displayUrl}
                </div>
              </div>
            </div>

            {partner.description && (
              <p className="partner-desc small">{partner.description}</p>
            )}

            <div className="partner-items-head">
              <strong>{partner.items.length}</strong> Angebote erkannt
              {Object.keys(itemKindCounts).length > 0 && (
                <span className="small muted">
                  {" — "}
                  {Object.entries(itemKindCounts)
                    .map(([k, v]) => `${v}× ${k}`)
                    .join(", ")}
                </span>
              )}
            </div>
            <div className="partner-items">
              {partner.items.slice(0, 12).map((it, i) => (
                <div className="partner-item" key={`${it.name}-${i}`}>
                  {it.imageUrl ? (
                    <img
                      src={it.imageUrl}
                      alt={it.name}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="partner-item-noimg">{it.kind.charAt(0)}</div>
                  )}
                  <div className="partner-item-body">
                    <div className="partner-item-name" title={it.name}>
                      {it.name}
                    </div>
                    <div className="partner-item-sub small muted">
                      {it.priceLabel ?? it.kind}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="partner-mode-row">
              <div className="partner-mode-tabs">
                <button
                  className={`partner-mode-tab ${mode === "clone" ? "active" : ""}`}
                  onClick={() => setMode("clone")}
                  disabled={applying}
                  type="button"
                >
                  📄 Neu klonen
                  <span className="small muted">
                    Original bleibt erhalten
                  </span>
                </button>
                <button
                  className={`partner-mode-tab ${mode === "apply" ? "active" : ""}`}
                  onClick={() => setMode("apply")}
                  disabled={applying}
                  type="button"
                >
                  ✏️ Direkt überschreiben
                  <span className="small muted">
                    Reel wird in-place geändert
                  </span>
                </button>
              </div>
              {mode === "clone" && (
                <input
                  className="partner-input"
                  placeholder="Titel des Klons"
                  value={cloneTitle}
                  onChange={(e) => setCloneTitle(e.target.value)}
                  disabled={applying}
                />
              )}
            </div>

            <div className="modal-actions">
              <button className="btn ghost" onClick={onClose} disabled={applying}>
                Abbrechen
              </button>
              <button
                className="btn primary"
                onClick={() => void onConfirm()}
                disabled={applying}
              >
                {applying
                  ? "…"
                  : mode === "clone"
                    ? "Klon anlegen"
                    : "Reel überschreiben"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
